import React, { useState } from 'react';
import { Tooltip, Select } from 'antd';
import { PlusOutlined, LoadingOutlined, CheckOutlined } from '@ant-design/icons';
import { useTaskDrawer } from '../TaskDrawerContext';
import { getFullName } from '../utils/personUtils';
import Avatar from './Avatar';

export default function TaskFooter() {
    const {
        collaborators, updatingTask, handleUpdateTask, mode, handleCreateTask,
        task, itemType, editTitle, editDescription, editPriority, editEstHours, editEstMinutes,
        handleAddCollaborator, handleRemoveCollaborator, availableEmployees,
        isAutoSaving, statusUpdating, milestoneId,
    } = useTaskDrawer();

    const [showCollabAdder, setShowCollabAdder] = useState(false);
    const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');
    const isDraftTask = mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft';

    // Validation for create / draft mode — returns list of missing field labels
    const getMissingFields = () => {
        const missing = [];
        if (!editTitle.trim() || editTitle.trim() === 'Untitled Draft') missing.push('Title');
        if (!task?.project?._id && !task?.project) missing.push('Project');
        if (!task?.dueDate) missing.push('Due Date');
        if (!isMilestone) {
            if (!editDescription.trim()) missing.push('Description');
            if (!task?.assignee?._id && !task?.assignee) missing.push('Assignee');
            if (!editPriority) missing.push('Priority');
            if (!task?.status && !isDraftTask) missing.push('Status');
            if (!task?.startDate) missing.push('Start Date');
            if (!editEstHours && !editEstMinutes) missing.push('Estimated Time');
            if (!milestoneId && !task?.milestoneId) missing.push('Milestone');
        }
        return missing;
    };

    const missingFields = isDraftTask ? getMissingFields() : [];
    const isCreateDisabled = missingFields.length > 0;

    return (
        <footer className="px-4 py-2 bg-white dark:bg-[#1f1f1f] border-t border-[#e7e8e9] dark:border-[#303030] flex justify-between items-center shrink-0">
            {/* Collaborator avatars + compact add button */}
            <div className="flex items-center gap-2">
                {!isMilestone && (
                    <div className="flex -space-x-1.5 items-center">
                        {collaborators.slice(0, 5).map((collaborator) => (
                            <Tooltip
                                key={collaborator._id || collaborator}
                                title={`Collaborator: ${getFullName(collaborator)}`}
                                placement="top"
                            >
                                <div className="ring-1.5 ring-white dark:ring-[#303030] flex-shrink-0 overflow-hidden cursor-pointer" style={{ borderRadius: '50%' }}>
                                    <Avatar person={collaborator} size={24} />
                                </div>
                            </Tooltip>
                        ))}
                        {collaborators.length > 5 && (
                            <Tooltip title={collaborators.slice(5).map(c => getFullName(c)).join(', ')} placement="top">
                                <div className="w-6 h-6 ring-1.5 ring-white dark:ring-[#303030] bg-[#e7e8e9] dark:bg-[#262626] flex items-center justify-center text-[9px] font-bold text-[#1c1e21] dark:text-white flex-shrink-0 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors" style={{ borderRadius: '50%' }}>
                                    +{collaborators.length - 5}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}

                {/* + Add Collaborators compact button — view/edit mode only */}
                {!isMilestone && mode !== 'create' && (
                    <div className="relative">
                        {showCollabAdder ? (
                            <Select
                                showSearch
                                autoFocus
                                placeholder="Add collaborator…"
                                optionFilterProp="label"
                                size="small"
                                className="w-44"
                                onSelect={(val) => {
                                    handleAddCollaborator(val);
                                    setShowCollabAdder(false);
                                }}
                                onBlur={() => setShowCollabAdder(false)}
                                options={(availableEmployees || []).map(e => ({
                                    label: getFullName(e),
                                    value: e._id,
                                }))}
                            />
                        ) : (
                            <Tooltip title="Add Collaborator" placement="top">
                                <button
                                    onClick={() => setShowCollabAdder(true)}
                                    className="flex items-center gap-1 text-xs font-bold text-[#03488a] dark:text-[#a7c8ff] hover:bg-blue-50 dark:hover:bg-blue-950/40 px-2 py-1 rounded-lg transition-colors"
                                >
                                    <PlusOutlined style={{ fontSize: 11 }} />
                                    <span>Add</span>
                                </button>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>

            <div className="flex flex-col items-end gap-1.5">
                {/* Missing fields hint — shown in create or draft mode when something is still needed */}
                {isDraftTask && missingFields.length > 0 && (
                    <p className="text-[11px] text-red-500 dark:text-red-400 font-medium text-right">
                        Still required: {missingFields.join(' · ')}
                    </p>
                )}

                {isDraftTask ? (
                    <button
                        data-tour="create-task-submit-btn"
                        onClick={handleCreateTask}
                        disabled={updatingTask || isCreateDisabled}
                        title={isCreateDisabled ? `Missing: ${missingFields.join(', ')}` : ''}
                        className="px-6 py-2 text-white rounded-lg font-bold text-sm active:scale-95 transition-transform flex items-center gap-2 shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                    >
                        <span className="material-symbols-outlined text-sm">{isMilestone ? 'diamond' : 'add'}</span>
                        {updatingTask
                            ? (isMilestone ? 'Creating Milestone...' : 'Creating Task...')
                            : (isMilestone ? '◆ Create Milestone' : 'Create Task')
                        }
                    </button>
                ) : (
                    <div className="flex gap-3 items-center">
                        {/* Auto-save indicator with live spinner */}
                        {(isAutoSaving || updatingTask || statusUpdating) ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#03488a] dark:text-[#a7c8ff]">
                                <LoadingOutlined spin className="text-xs" />
                                <span>Saving changes…</span>
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 font-medium">
                                <CheckOutlined className="text-[11px] text-emerald-500 font-bold" />
                                <span>Auto-saved</span>
                            </span>
                        )}
                        <button disabled className="px-4 py-2 text-slate-400 font-bold text-sm hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-not-allowed opacity-40">Archive</button>
                    </div>
                )}
            </div>
        </footer>
    );
}
